// mui
import {
  DataGrid,
  GridColDef,
  gridPageCountSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { Box, Pagination, Stack } from "@mui/material";
// components
import NoItems from "../alerts/NoItems";
// hooks
import usePageParams from "../../hooks/usePageParams";

type Props<T> = {
  rows: T[];
  columns: GridColDef[];
  itemsPerPage?: number;
  totalItems?: number;
  // Column-header click-to-sort is client-side only in DataGrid's community
  // tier. That's fine when every row is actually loaded (rare here — see
  // JobApplicants, which fetches with `&all` and sets itemsPerPage to the
  // full row count), but for every server-paginated table (the default —
  // paginationMode is always "server" below) it would silently sort only
  // the 20 visible rows while claiming to sort the column, which is wrong
  // and confusing next to the real (server-side) sort control, SortBox.
  // Off by default; opt in only where the full dataset is actually in memory.
  sortable?: boolean;
};

const CustomPagination = () => {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const { page, setPage } = usePageParams();
  return (
    <Pagination
      // sx={{direction:MuiTheme.direction}}
      color="primary"
      count={pageCount}
      page={page}
      defaultPage={1}
      onChange={(event, value) => {
        apiRef.current.setPage(value);
        setPage(value);
      }}
      sx={{
        "& .MuiButtonBase-root": {
          direction: "ltr !important",
        },
      }}
    />
  );
};

const DataTable = <T,>({
  rows,
  columns,
  itemsPerPage = 20,
  totalItems = 20,
  sortable = false,
}: Props<T>) => {
  const { page } = usePageParams();
  if (rows.length === 0) return <NoItems />;
  return (
    <Box>
      <DataGrid
        getRowId={(row) => row.id}
        rows={rows}
        columns={columns}
        pagination
        paginationMode="server"
        paginationModel={{
          page: page - 1,
          pageSize: itemsPerPage,
        }}
        autoHeight
        rowCount={totalItems}
        disableColumnSorting={!sortable}
        slots={{ pagination: CustomPagination }}
      />
    </Box>
  );
};

export default DataTable;
